import { useEffect, type CSSProperties } from "react";
import { useTranslation } from "react-i18next";
import { Avatar, Badge, Button, Card } from "@/design-system";
import { Icon } from "@/components/Icon";
import { useLessonStudents } from "@/api/queries";
import { formatDays, formatDuration, formatLongDate, formatTime } from "@/lib/format";
import { instructorLabel } from "@/lib/instructor";
import { displayName } from "@/lib/user";
import { useOnline } from "@/lib/useOnline";
import { OfflineErrorState } from "@/components/OfflineErrorState";
import { SHEET_OVERLAY_STYLE, SHEET_STYLE } from "@/components/sheetStyles";
import type { Lesson, Student } from "@/lib/types";

/**
 * LessonDetailSheet — the teacher's read-oriented detail view for a
 * selected lesson (issue #222).
 *
 * Opens as a bottom sheet when the teacher taps a lesson card. It shows
 * everything carried by the lesson (time, duration, type, instructor,
 * capacity, booking window, description and series marker) and — the
 * point of the issue — the people who reserved a spot. The roster comes
 * from the same `GET /api/lessons/:id/students` endpoint the enrollment
 * picker uses, which returns the active (`:booked`) reservations for
 * any lesson type, so `:open` self-booked classes and teacher-managed
 * `:assigned` classes are both covered.
 *
 * For `:assigned` lessons the sheet hands off to the existing
 * `StudentPickerDialog` via `onManageStudents`; `:open` rosters are
 * read-only because students book those themselves.
 */

export function LessonDetailSheet({
  lesson,
  token,
  onClose,
  onManageStudents,
  onOpenStudent,
}: {
  lesson: Lesson;
  token: string | null;
  onClose: () => void;
  /** Only provided for `:assigned` lessons — opens the enrollment picker. */
  onManageStudents?: () => void;
  /** Tapping a roster name/avatar opens that student's details + notes. */
  onOpenStudent?: (student: Student) => void;
}) {
  const { t } = useTranslation();
  const online = useOnline();
  const rosterQuery = useLessonStudents(token, lesson.id);
  const students: Student[] = rosterQuery.data ?? [];

  const isAssigned = lesson.lesson_type === "assigned";
  const full = lesson.spots_left <= 0;
  const overCapacity = lesson.spots_taken > lesson.capacity;
  const pct =
    lesson.capacity > 0
      ? Math.min(Math.round((lesson.spots_taken / lesson.capacity) * 100), 100)
      : 0;

  // Escape closes the sheet, matching the rest of the app's overlays.
  useEffect(() => {
    function onKey(event: KeyboardEvent) {
      if (event.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t("teacher.detailHeading")}
      data-testid="lesson-detail-sheet"
      style={SHEET_OVERLAY_STYLE}
      onClick={onClose}
    >
      <Card
        padding="var(--space-5)"
        onClick={(e) => e.stopPropagation()}
        style={SHEET_STYLE}
      >
        {/* header */}
        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 12,
          }}
        >
          <div style={{ minWidth: 0 }}>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 8 }}>
              {isAssigned ? (
                <Badge tone="info" data-testid="lesson-detail-type">
                  {t("teacher.typeAssigned")}
                </Badge>
              ) : (
                <Badge tone="neutral" data-testid="lesson-detail-type">
                  {t("teacher.typeOpen")}
                </Badge>
              )}
              {lesson.recurrence_key && (
                <Badge tone="neutral" data-testid="lesson-detail-series">
                  {t("teacher.seriesTag")}
                </Badge>
              )}
            </div>
            <div
              data-testid="lesson-detail-title"
              style={{
                font: "var(--fw-extra) var(--fs-2xl)/1.1 var(--font-display)",
                color: "var(--text-strong)",
                letterSpacing: "var(--ls-tight)",
              }}
            >
              {lesson.title}
            </div>
            <div
              style={{
                font: "var(--fw-regular) var(--fs-sm)/1.4 var(--font-body)",
                color: "var(--text-muted)",
                marginTop: 6,
              }}
            >
              {formatLongDate(lesson.starts_at)} · {formatTime(lesson.starts_at)} ·{" "}
              {formatDuration(lesson.duration_minutes)}
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            data-testid="lesson-detail-close"
          >
            {t("common.close")}
          </Button>
        </div>

        {/* meta */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            marginTop: "var(--space-4)",
          }}
        >
          <Avatar name={instructorLabel(lesson)} size={40} ring />
          <div style={{ minWidth: 0 }}>
            <div
              style={{
                font: "var(--fw-semibold) var(--fs-2xs)/1 var(--font-body)",
                letterSpacing: "var(--ls-wide)",
                textTransform: "uppercase",
                color: "var(--text-faint)",
              }}
            >
              {t("teacher.detailInstructor")}
            </div>
            <div
              style={{
                font: "var(--fw-bold) var(--fs-sm)/1.2 var(--font-display)",
                color: "var(--text-strong)",
                marginTop: 4,
              }}
            >
              {instructorLabel(lesson)}
            </div>
          </div>
        </div>

        {/* description */}
        <p
          data-testid="lesson-detail-description"
          style={{
            font: "var(--fw-regular) var(--fs-sm)/1.6 var(--font-body)",
            color: "var(--text-muted)",
            margin: "var(--space-4) 0 0",
          }}
        >
          {lesson.description?.trim() || t("teacher.detailDescriptionFallback")}
        </p>

        {/* capacity */}
        <div style={{ marginTop: "var(--space-5)" }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "baseline",
              marginBottom: 6,
            }}
          >
            <span
              style={{
                font: "var(--fw-semibold) var(--fs-2xs)/1 var(--font-body)",
                letterSpacing: "var(--ls-wide)",
                textTransform: "uppercase",
                color: "var(--text-faint)",
              }}
            >
              {t("teacher.detailCapacity")}
            </span>
            <span
              data-testid="lesson-detail-fill"
              style={{
                font: "var(--fw-bold) var(--fs-sm)/1 var(--font-mono)",
                color: "var(--text-strong)",
              }}
            >
              {t("teacher.fill", {
                taken: lesson.spots_taken,
                capacity: lesson.capacity,
              })}
            </span>
          </div>
          <div
            aria-hidden
            style={{
              height: 8,
              borderRadius: 999,
              background: "var(--surface-muted)",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                width: `${pct}%`,
                height: "100%",
                borderRadius: 999,
                background: full ? "var(--rust-500)" : "var(--brand)",
                transition: "width var(--dur-base) var(--ease-out)",
              }}
            />
          </div>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "baseline",
              marginTop: 8,
              font: "var(--fw-regular) var(--fs-2xs)/1.4 var(--font-body)",
              color: "var(--text-faint)",
            }}
          >
            <span>
              {lesson.booking_open_days !== null &&
              lesson.booking_open_days !== undefined
                ? t("teacher.windowMeta", {
                    open: formatDays(lesson.booking_open_days),
                    close: formatDuration(lesson.booking_cutoff_minutes),
                  })
                : t("teacher.cutoffMeta", {
                    duration: formatDuration(lesson.booking_cutoff_minutes),
                  })}
            </span>
            <Badge tone={full ? "danger" : "success"} dot>
              {full ? t("teacher.full") : t("teacher.open", { count: lesson.spots_left })}
            </Badge>
          </div>
          {overCapacity && (
            <div
              role="alert"
              data-testid="lesson-detail-over-capacity"
              style={{
                marginTop: 10,
                padding: "var(--space-2) var(--space-4)",
                background: "var(--status-warning-bg)",
                color: "var(--status-warning-fg)",
                borderRadius: "var(--radius-md)",
                font: "var(--fw-medium) var(--fs-xs)/1.4 var(--font-body)",
              }}
            >
              {t("teacher.overCapacity", {
                enrolled: lesson.spots_taken,
                capacity: lesson.capacity,
              })}
            </div>
          )}
        </div>

        {/* roster */}
        <div
          style={{
            marginTop: "var(--space-5)",
            borderTop: "1px solid var(--border-subtle)",
            paddingTop: "var(--space-4)",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              marginBottom: 10,
            }}
          >
            <span
              style={{
                font: "var(--fw-semibold) var(--fs-2xs)/1 var(--font-body)",
                letterSpacing: "var(--ls-wide)",
                textTransform: "uppercase",
                color: "var(--text-faint)",
              }}
            >
              {isAssigned
                ? t("teacher.detailRosterAssignedHeading")
                : t("teacher.detailRosterHeading")}
            </span>
            <Badge
              tone="neutral"
              data-testid="lesson-detail-roster-count"
            >
              {rosterQuery.isSuccess ? students.length : lesson.spots_taken}
            </Badge>
          </div>

          {rosterQuery.isLoading && (
            <div
              data-testid="lesson-detail-roster-loading"
              style={{ color: "var(--text-faint)", font: "var(--text-body-base)" }}
            >
              {t("teacher.detailRosterLoading")}
            </div>
          )}

          {!rosterQuery.isLoading && rosterQuery.isError && (
            online ? (
              <div
                role="alert"
                data-testid="lesson-detail-roster-error"
                style={{
                  background: "var(--status-danger-bg)",
                  color: "var(--status-danger-fg)",
                  padding: "var(--space-3) var(--space-4)",
                  borderRadius: "var(--radius-md)",
                  font: "var(--fw-medium) var(--fs-sm)/1.4 var(--font-body)",
                }}
              >
                {rosterQuery.error?.message ?? t("teacher.detailRosterError")}
              </div>
            ) : (
              <OfflineErrorState />
            )
          )}

          {!rosterQuery.isLoading &&
            !rosterQuery.isError &&
            students.length === 0 && (
              <div
                data-testid="lesson-detail-roster-empty"
                style={{ color: "var(--text-faint)", font: "var(--text-body-base)" }}
              >
                {isAssigned
                  ? t("teacher.detailRosterEmptyAssigned")
                  : t("teacher.detailRosterEmpty")}
              </div>
            )}

          {!rosterQuery.isLoading && !rosterQuery.isError && students.length > 0 && (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {students.map((student) => {
                const identityStyle: CSSProperties = {
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  flex: 1,
                  minWidth: 0,
                  padding: 0,
                  background: "none",
                  border: "none",
                  textAlign: "left",
                  color: "inherit",
                  cursor: onOpenStudent ? "pointer" : "default",
                };

                const identity = (
                  <>
                    <Avatar name={displayName(student)} size={36} />
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div
                        style={{
                          font: "var(--fw-semibold) var(--fs-sm)/1.2 var(--font-body)",
                          color: "var(--text-strong)",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {displayName(student)}
                      </div>
                      <div
                        style={{
                          font: "var(--fw-regular) var(--fs-2xs)/1.4 var(--font-body)",
                          color: "var(--text-muted)",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {student.email}
                      </div>
                    </div>
                  </>
                );

                return (
                  <div
                    key={student.id}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 12,
                      padding: "var(--space-2) var(--space-3)",
                      background: "var(--surface-muted)",
                      borderRadius: "var(--radius-md)",
                    }}
                  >
                    {onOpenStudent ? (
                      <button
                        type="button"
                        data-testid={`lesson-detail-student-${student.id}`}
                        onClick={() => onOpenStudent(student)}
                        style={identityStyle}
                      >
                        {identity}
                      </button>
                    ) : (
                      <div
                        data-testid={`lesson-detail-student-${student.id}`}
                        style={identityStyle}
                      >
                        {identity}
                      </div>
                    )}
                    <span
                      style={{
                        flex: "none",
                        font: "var(--fw-regular) var(--fs-2xs)/1 var(--font-body)",
                        color: "var(--text-faint)",
                      }}
                    >
                      {t("teacher.upcomingBookings", {
                        count: student.upcoming_bookings,
                      })}
                    </span>
                  </div>
                );
              })}
            </div>
          )}

          <div
            style={{
              marginTop: 12,
              font: "var(--fw-regular) var(--fs-2xs)/1.5 var(--font-body)",
              color: "var(--text-faint)",
            }}
          >
            {isAssigned ? t("teacher.detailAssignedNote") : t("teacher.detailOpenNote")}
          </div>

          {onManageStudents && (
            <Button
              variant="secondary"
              size="sm"
              fullWidth
              onClick={onManageStudents}
              iconLeft={<Icon name="users" size={18} />}
              style={{ marginTop: 12 }}
              data-testid="lesson-detail-manage"
            >
              {t("teacher.detailManage")}
            </Button>
          )}
        </div>
      </Card>
    </div>
  );
}
